/**
 * Paperwork by device (2026-10-09, Litsey Creek #092678280; mockup
 * threads/paperwork-by-device/mockup.html).
 *
 * The Paperwork card lists FILES, newest first. On a 33-device job that is 58
 * rows of "Passed SN-5794xx …pdf", and nobody can tell from it which device is
 * missing its test or its report, or which has two. This strip turns it
 * around: one tile per device, its serial big, and two marks:
 *
 *   TEST    ✓ passed (green)  ✗ failed (red)  ○ none (grey)
 *   REPORT  ✓ one (green)     ✗ none while tested (red)  2 copies (amber)
 *
 * Problem tiles sort first and get a colored edge; "Only problems" hides the
 * rest. A device that was tested on this job but isn't on it any more shows
 * as "not on job" (Litsey: 579411, unlinked after its test was entered).
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/** The categories that are a device's report. */
export const DEVICE_REPORT_CATEGORIES = ["test_report_official", "test_report_paper", "test_report"] as const;

export interface DeviceIn {
    assetId: string;
    serial: string | null;
    size: string | null;
    assemblyType: string | null;
}
export interface TestIn {
    assetId: string;
    serial: string | null;
    result: string | null;
}
export interface AttachmentIn {
    asset_id: string | null;
    category: string | null;
    archived_at?: string | null;
}

export type TestMark = "pass" | "fail" | "none";
export type ReportMark = "one" | "none" | "copies" | "not_needed";

export interface DeviceTile {
    assetId: string;
    serial: string;
    label: string;
    test: TestMark;
    report: ReportMark;
    reportCount: number;
    onJob: boolean;
    problem: boolean;
}

/** Pure: devices + tests + files → one tile per device, problems first. */
export function buildDeviceTiles(devices: DeviceIn[], tests: TestIn[], files: AttachmentIn[]): DeviceTile[] {
    const reports = new Map<string, number>();
    for (const f of files) {
        if (!f.asset_id || f.archived_at) continue;
        if (!(DEVICE_REPORT_CATEGORIES as readonly string[]).includes(f.category ?? "")) continue;
        reports.set(f.asset_id, (reports.get(f.asset_id) ?? 0) + 1);
    }
    const testBy = new Map(tests.map((t) => [t.assetId, t]));
    const all: Array<DeviceIn & { onJob: boolean }> = [
        ...devices.map((d) => ({ ...d, onJob: true })),
        ...tests
            .filter((t) => !devices.some((d) => d.assetId === t.assetId))
            .map((t) => ({ assetId: t.assetId, serial: t.serial, size: null, assemblyType: null, onJob: false })),
    ];
    const tiles = all.map((d): DeviceTile => {
        const r = (testBy.get(d.assetId)?.result ?? "").toLowerCase();
        const test: TestMark = r === "pass" ? "pass" : r === "fail" ? "fail" : "none";
        const n = reports.get(d.assetId) ?? 0;
        const report: ReportMark = n > 1 ? "copies" : n === 1 ? "one" : test === "none" ? "not_needed" : "none";
        const problem = !d.onJob || test === "none" || report === "none" || report === "copies" || test === "fail";
        return {
            assetId: d.assetId,
            serial: d.serial ?? "—",
            label: [d.size, d.assemblyType?.toUpperCase()].filter(Boolean).join(" "),
            test,
            report,
            reportCount: n,
            onJob: d.onJob,
            problem,
        };
    });
    return tiles.sort((a, b) => Number(b.problem) - Number(a.problem) || a.serial.localeCompare(b.serial));
}

export function tileCounts(tiles: DeviceTile[]) {
    return {
        devices: tiles.filter((t) => t.onJob).length,
        tested: tiles.filter((t) => t.test !== "none").length,
        reported: tiles.filter((t) => t.report === "one" || t.report === "copies").length,
        problems: tiles.filter((t) => t.problem).length,
    };
}

const MARK = {
    pass: { sym: "✓", cls: "bg-emerald-600 text-white" },
    fail: { sym: "✗", cls: "bg-red-600 text-white" },
    none: { sym: "○", cls: "bg-muted text-muted-foreground" },
} as const;

function ReportBadge({ t }: { t: DeviceTile }) {
    if (t.report === "one")
        return <span className={cn("grid h-6 w-6 place-items-center rounded-full font-bold", MARK.pass.cls)}>✓</span>;
    if (t.report === "copies")
        return (
            <span className="grid h-6 min-w-6 place-items-center rounded-full bg-amber-500 px-1 text-xs font-extrabold text-white">
                {t.reportCount}
            </span>
        );
    if (t.report === "none")
        return <span className={cn("grid h-6 w-6 place-items-center rounded-full font-bold", MARK.fail.cls)}>✗</span>;
    return <span className={cn("grid h-6 w-6 place-items-center rounded-full font-bold", MARK.none.cls)}>○</span>;
}

export function DevicePaperwork({ jobId, devices, tests }: { jobId: string; devices: DeviceIn[]; tests: TestIn[] }) {
    const [onlyProblems, setOnlyProblems] = useState(false);
    const filesQ = useQuery({
        queryKey: ["job", jobId, "device-paperwork"],
        enabled: !!jobId && devices.length + tests.length > 0,
        queryFn: () => api.get<{ attachments: AttachmentIn[] }>(`/api/attachments?jobId=${jobId}&limit=200`),
    });
    const tiles = useMemo(
        () => buildDeviceTiles(devices, tests, filesQ.data?.attachments ?? []),
        [devices, tests, filesQ.data],
    );
    if (tiles.length < 2) return null;
    const c = tileCounts(tiles);
    const shown = onlyProblems ? tiles.filter((t) => t.problem) : tiles;

    return (
        <div className="rounded-xl border border-border bg-card p-3" data-testid="device-paperwork">
            <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="text-xs font-extrabold uppercase tracking-wide text-muted-foreground">By device</span>
                <span className="text-sm">
                    <b className="text-lg tabular-nums">{c.devices}</b> devices
                </span>
                <span className="text-sm">
                    <b className="text-lg tabular-nums text-emerald-700 dark:text-emerald-400">{c.tested}</b> tested
                </span>
                <span className="text-sm">
                    <b className="text-lg tabular-nums text-emerald-700 dark:text-emerald-400">{c.reported}</b> with a
                    report
                </span>
                {c.problems > 0 && (
                    <button
                        type="button"
                        onClick={() => setOnlyProblems((v) => !v)}
                        aria-pressed={onlyProblems}
                        className={cn(
                            "ml-auto rounded-full border px-2.5 py-0.5 text-xs font-bold",
                            onlyProblems
                                ? "border-red-600 bg-red-600 text-white"
                                : "border-red-300 text-red-700 dark:border-red-800 dark:text-red-400",
                        )}
                    >
                        {c.problems} need a look{onlyProblems ? " · show all" : ""}
                    </button>
                )}
            </div>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
                {shown.map((t) => (
                    <div
                        key={t.assetId}
                        title={[
                            `S/N ${t.serial}`,
                            t.test === "none" ? "no test" : `test ${t.test}`,
                            t.report === "copies"
                                ? `${t.reportCount} reports`
                                : t.report === "one"
                                  ? "report on file"
                                  : t.report === "none"
                                    ? "no report"
                                    : "",
                            t.onJob ? "" : "not on this job",
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                        className={cn(
                            "flex items-center gap-1.5 rounded-lg border px-2 py-1.5",
                            t.problem ? "border-l-4 border-l-red-500 border-y-border border-r-border" : "border-border",
                            !t.onJob && "border-l-purple-500",
                        )}
                    >
                        <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-sm font-extrabold tabular-nums">{t.serial}</span>
                            <span className="truncate text-[10.5px] text-muted-foreground">
                                {t.onJob ? t.label || " " : "not on job"}
                            </span>
                        </div>
                        <span
                            className={cn("grid h-6 w-6 place-items-center rounded-full font-bold", MARK[t.test].cls)}
                            aria-label={`test ${t.test}`}
                        >
                            {MARK[t.test].sym}
                        </span>
                        <ReportBadge t={t} />
                    </div>
                ))}
            </div>
            <div className="mt-1.5 text-[10.5px] text-muted-foreground">First mark: test · second: report</div>
        </div>
    );
}
